import * as ngCommonHttp from '@angular/common/http';

import { Injectable } from '@angular/core';
import { ProductGroup, ProductGroupsResponse } from '@interfaces/product-group/1.0';
import {
  ModifierGroupOverride,
  Product,
  ProductOverrideInput,
  ProductsResponse,
} from '@interfaces/product/1.0';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap, toArray } from 'rxjs/operators';

import { AbstractProductService } from '@abstract-services/product/1.0/product.abstract-service';
import { CatalogEntriesAPI, ProductsAPI } from '@constants/api';
import { CatalogItemType } from '@constants/catalogItem/1.0';
import { CATALOG_PAGE_SIZE_PRODUCTS_AND_COMBOS } from '@constants/pagination/1.0';
import { CatalogItemsService } from '@services/catalog-items/1.0/catalog-items.service';
import { PaginationService } from '@services/pagination/1.0/pagination.service';

@Injectable({
  providedIn: 'root',
})
export class ProductService extends AbstractProductService {
  productsApiUrl;
  productPutApiUrl;
  productPatchApiUrl;
  productGroupsByProductGetApiUrl;
  itemType = CatalogItemType.PRODUCT;

  // save the list of products after fetching but keep track of which brand this is for so we can reset it
  cachedBrandId: string;
  fetchReplay: Observable<Product[]>;

  // Copy state management
  private isCopyingSubject = new BehaviorSubject<boolean>(false);
  public isCopying$ = this.isCopyingSubject.asObservable();

  constructor(
    private http: ngCommonHttp.HttpClient,
    private paginationService: PaginationService,
    private catalogItemsService: CatalogItemsService,
  ) {
    super();
    this.productsApiUrl = ProductsAPI.GET;
    this.productPutApiUrl = ProductsAPI.PUT;
    this.productPatchApiUrl = CatalogEntriesAPI.PATCH;
    this.productGroupsByProductGetApiUrl = ProductsAPI.GET_BY_PRODUCT;
  }

  getProducts(brandId: string, includeDeleted: boolean = false): Observable<Product[]> {
    // reset cache if brand changed
    if (this.cachedBrandId !== brandId) {
      this.catalogItemsService.resetMasterCache();
      this.cachedBrandId = brandId;
      this.fetchReplay = undefined;
    }

    const cached = this.catalogItemsService.getCache(this.itemType);
    if (!cached) {
      // mark cache as pending
      this.catalogItemsService.updateCache(this.itemType);

      let url =
        this.productsApiUrl.replace(':tenantId', brandId) + `?includeDeleted=${includeDeleted}`;

      // fetchAllPages emits each page's ProductsResponse
      const all$ = this.paginationService
        .fetchAllPages<ProductsResponse>(url, { pageSize: CATALOG_PAGE_SIZE_PRODUCTS_AND_COMBOS })
        .pipe(
          // collect all page responses into an array
          toArray(),
          map((pages: ProductsResponse[]) => {
            return pages
              .filter((r) => r && r._embedded && r._embedded.products)
              .reduce<Product[]>((acc, r) => acc.concat(r._embedded.products), []);
          }),
          // cache once with the full list
          tap((products: Product[]) => {
            this.catalogItemsService.updateCache(this.itemType, products);
          }),
          shareReplay(1),
          catchError((err: ngCommonHttp.HttpErrorResponse) => {
            this.catalogItemsService.clearCache(this.itemType);
            return throwError(err);
          }),
        );

      return (this.fetchReplay = all$);
    }

    if (cached.length === 0) {
      return this.fetchReplay!;
    }

    return of(cached);
  }

  /**
   * Get Product Groups by Product catalogEntryId
   *
   * @param catalogEntryId - id of Product
   */
  getProductGroupsByProduct(catalogEntryId: string): Observable<ProductGroup[]> {
    const url = this.productGroupsByProductGetApiUrl.replace(':catalogEntryId', catalogEntryId);
    return this.http.get(url).pipe(
      map((response: ProductGroupsResponse) => {
        if (response && response._embedded) {
          return response._embedded.productGroups;
        } else {
          return undefined;
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => {
        return throwError(err);
      }),
    );
  }

  /**
   * create
   *
   * @param product [Partial<Product>] single Product
   * @returns observable of current single Product
   */
  create(product: Partial<Product>, brandId: string): Observable<Product> {
    const BODY = { ...product };
    if (!BODY.modifierGroupIds) {
      BODY.modifierGroupIds = [];
    }

    const url = this.productsApiUrl.replace(':tenantId', brandId);
    return this.http.post(url, BODY).pipe(
      map((response: Product) => {
        return response;
      }),
      tap((p: Product) => {
        if (this.cachedBrandId === brandId && this.catalogItemsService.getCache(this.itemType)) {
          this.catalogItemsService.addItemCache(p, this.itemType);
        }
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  save(catalogEntryId: string, product: Partial<Product>, remove: string[] = []): Observable<any> {
    const url = `${this.productPatchApiUrl}/${catalogEntryId}`;
    const payload = {
      set: {
        ...product,
      },
      remove,
    };
    return this.http.patch(url, payload).pipe(
      tap((response: any) => {
        const itemToCache: any = { ...product };
        if (response && response.nutritionTexts) {
          itemToCache.nutritionTexts = response.nutritionTexts;
        }
        this.catalogItemsService.updateItemCache(itemToCache, this.itemType);
      }),
      map((response: any) => {
        const returnedProduct: any = { ...product };
        if (response && response.nutritionTexts) {
          returnedProduct.nutritionTexts = response.nutritionTexts;
        }
        return returnedProduct;
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  saveOverrides(catalogEntryId: string, overrides: ModifierGroupOverride[]): Observable<Product> {
    const url = `${this.productPutApiUrl}${catalogEntryId}/overrides`;
    const BODY: ProductOverrideInput = {
      payload: overrides,
    };
    return this.http.put(url, BODY).pipe(
      map((response: Product) => {
        return response;
      }),
      tap((p: Product) => {
        this.catalogItemsService.updateItemCache(p, this.itemType);
      }),
      catchError((err: ngCommonHttp.HttpErrorResponse) => throwError(err)),
    );
  }

  /**
   * Set the copying state for product operations
   * @param isCopying - whether a product is currently being copied
   */
  setCopyingState(isCopying: boolean): void {
    this.isCopyingSubject.next(isCopying);
  }

  /**
   * Get the current copying state
   * @returns boolean indicating if a product is currently being copied
   */
  getCopyingState(): boolean {
    return this.isCopyingSubject.value;
  }
}
